import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DragDropModule, CdkDragMove, CdkDragEnd } from '@angular/cdk/drag-drop';
import { ImageUploadConfig } from '../../../config/imageUploadConfig';
import { ImageMetadata, EditorTransformState, transformImageWithEditorState } from '../../../utils/image-editor.util';

@Component({
  selector: 'app-image-editor-modal',
  standalone: true,
  imports: [CommonModule, DragDropModule],
  templateUrl: './image-editor-modal.component.html',
  styleUrls: ['./image-editor-modal.component.scss'],
})
export class ImageEditorModalComponent implements OnInit, OnDestroy {
  @Input() originalFile!: File;
  @Input() config!: ImageUploadConfig;
  @Input() metadata!: ImageMetadata;
  @Input() fixReason: string = '';

  @Output() saved = new EventEmitter<File>();
  @Output() cancelled = new EventEmitter<void>();

  @ViewChild('viewport', { static: false }) viewportRef!: ElementRef<HTMLDivElement>;

  originalUrl = '';
  isProcessing = false;
  processingError = '';
  qualityStatusText = 'Original / Lossless';
  hasUnsavedChanges = false;
  
  // Simulated output size and preview url
  outputSizeKb: number = 0;
  outputFormat: string = 'Unknown';
  
  state: EditorTransformState = {
    zoom: 1.0,
    rotation: 0,
    focalX: 0.5,
    focalY: 0.5,
    backgroundMode: 'transparent',
    fitMode: 'contain',
  };

  // Base math ratios to control dragging and zoom constraints
  minZoom = 1.0;
  
  ngOnInit() {
    this.originalUrl = URL.createObjectURL(this.originalFile);
    this.outputSizeKb = Math.round(this.metadata.fileSizeBytes / 1024);
    this.outputFormat = this.metadata.mimeType.split('/')[1] || 'Unknown';
    
    // Set initial defaults
    this.state.fitMode = this.config.fitMode;
    this.state.backgroundMode = this.config.backgroundMode;
    // Initial calculate bounds
    this.resetState();
  }

  ngOnDestroy() {
    if (this.originalUrl) {
      URL.revokeObjectURL(this.originalUrl);
    }
  }

  resetState() {
    this.state = {
      zoom: 1.0,
      rotation: 0,
      focalX: 0.5,
      focalY: 0.5,
      backgroundMode: this.config.backgroundMode,
      fitMode: this.config.fitMode,
    };
    this.recalculateMinZoom();
    this.hasUnsavedChanges = false;
  }

  recalculateMinZoom() {
    // Minimum zoom logic
    // For COVER: min zoom must perfectly fill the canvas.
    // For CONTAIN: min zoom must perfectly fit inside the canvas.
    // We already calculate native target drawing sizes internally.
    this.minZoom = 1.0; 
    
    // reset focal to center on big ratio changes
    this.state.focalX = 0.5;
    this.state.focalY = 0.5;
  }
  
  zoomIn() {
    this.state.zoom += 0.1;
    this.hasUnsavedChanges = true;
    this.checkConstraints();
  }
  
  zoomOut() {
    this.state.zoom = Math.max(this.minZoom, this.state.zoom - 0.1);
    this.hasUnsavedChanges = true;
    this.checkConstraints();
  }

  rotate() {
    this.state.rotation = ((this.state.rotation + 90) % 360) as 0 | 90 | 180 | 270;
    this.hasUnsavedChanges = true;
    this.recalculateMinZoom();
    this.checkConstraints();
  }

  toggleBackground(mode: 'transparent' | 'white') {
    this.state.backgroundMode = mode;
    this.hasUnsavedChanges = true;
  }

  onDragMoved() {
    // Optional live feedback handling
  }

  onDragEnded(event: CdkDragEnd) {
    const el = event.source.getRootElement();
    const ts = el.style.transform;
    // CDK drag sets `transform: translate3d(x, y, 0)` on the element.
    // We want to apply this delta to our normalized focal points, and reset the DragDrop element's transform.
    
    const translateRegex = /translate3d\(([-\d.]+)px, ([-\d.]+)px/;
    const matches = translateRegex.exec(ts);
    
    if (matches && matches.length >= 3) {
      const deltaX = parseFloat(matches[1]);
      const deltaY = parseFloat(matches[2]);
      
      const viewport = this.viewportRef.nativeElement;
      const vw = viewport.clientWidth;
      const vh = viewport.clientHeight;
      
      // Map DOM delta pixels to normalized shifts
      // If we zoomed in (zoom > 1.0), the image is bigger than viewport.
      // E.g. image drawn 2x wider than viewport. A delta of +10px means moving focalX left by 10/drawnWidth.
      
      // Approximate physical image dimensions in DOM:
      let drawnW = vw;
      let drawnH = vh;
      
      const srcRatio = this.metadata.aspectRatio;
      const tgtRatio = vw / vh;
      
      if (this.state.fitMode === 'contain') {
        if (srcRatio > tgtRatio) { drawnH = vw / srcRatio; } else { drawnW = vh * srcRatio; }
      } else {
        if (srcRatio > tgtRatio) { drawnW = vh * srcRatio; } else { drawnH = vw / srcRatio; }
      }
      
      drawnW *= this.state.zoom;
      drawnH *= this.state.zoom;
      
      // Normalized shift: delta / excess width
      const excessW = drawnW - vw;
      const excessH = drawnH - vh;
      
      if (excessW > 0) {
        this.state.focalX = Math.max(0, Math.min(1, this.state.focalX - deltaX / excessW));
      }
      if (excessH > 0) {
        this.state.focalY = Math.max(0, Math.min(1, this.state.focalY - deltaY / excessH));
      }
    }
    
    // Reset CDK drag offset visually, since angular binds state to CSS top/left or css translates 
    event.source.reset();
    this.hasUnsavedChanges = true;
    this.checkConstraints();
  }

  checkConstraints() {
    // If not excess, force center
    if (this.state.zoom <= 1.0 && this.state.fitMode === 'cover') {
        this.state.focalX = 0.5;
        this.state.focalY = 0.5;
    }
  }

  async autoFix() {
    this.resetState();
    // In a sophisticated system, AutoFix might also try a test encode, 
    // but here we just snap to recommended math constraints and we simulate output.
    this.hasUnsavedChanges = true;
    this.processingError = '';
  }

  async useImage() {
    this.isProcessing = true;
    this.processingError = '';

    try {
      const { transform, qualityStatus } = await transformImageWithEditorState(
        this.originalFile, 
        this.config, 
        this.state
      );

      if (!transform.validationResult.isValid) {
        // Keep editor open, show errors
        const blocking = transform.validationResult.issues.find(i => i.severity === 'BLOCKING');
        this.processingError = blocking?.message || 'Invalid output generated. Please adjust crop or zoom.';
        this.isProcessing = false;
        return;
      }

      this.outputSizeKb = Math.round(transform.metadata.fileSizeBytes / 1024);
      this.qualityStatusText = qualityStatus;
      this.outputFormat = transform.metadata.mimeType.split('/')[1];
      
      // Delay slightly for UX before closing
      setTimeout(() => {
        this.saved.emit(transform.file);
      }, 400);

    } catch (e: any) {
      this.processingError = e.message || 'An error occurred generating the image.';
      this.isProcessing = false;
    }
  }

  cancel() {
    if (this.hasUnsavedChanges) {
      if (confirm('Discard your changes?')) {
        this.cancelled.emit();
      }
    } else {
      this.cancelled.emit();
    }
  }

  get containerAspect() {
    return this.config.targetRatio;
  }

  get imageStyle() {
    // When zoomed, the image spans larger than the container.
    // CSS naturally centers the object-fit container, so translate(0,0) is centered.
    // focal(0.5, 0.5) = perfectly centered = no extra translation.
    // focal(0, 0) = align top-left. We must shift the image positively (right/down) by the excess amount.
    // Excess ratio relative to container is (zoom - 1) / 2
    
    // Rotation alters which axis we are translating basically if we used fixed pixels, 
    // but percentages in CSS translate relate to the bounding box of the element itself.
    // An element 100% wide translated 10% moves by 10% of its width.
    
    // At zoom = 1, we can't pan.
    // At zoom > 1, the maximum pan distance is (zoom - 1) / (2 * zoom) * 100 %.
    // To match canvas math exactly where 0=Left, 0.5=Center, 1=Right:
    const maxTranslate = (this.state.zoom - 1) / (2 * this.state.zoom) * 100;
    
    // (0.5 - focal) returns a range from -0.5 to 0.5.
    // Multiply by 2.0 to get -1 to +1 multiplier for the max distance.
    const transX = (0.5 - this.state.focalX) * 2.0 * maxTranslate;
    const transY = (0.5 - this.state.focalY) * 2.0 * maxTranslate;

    return {
      'object-fit': this.state.fitMode,
      'transform': `scale(${this.state.zoom}) translate(${transX}%, ${transY}%) rotate(${this.state.rotation}deg)`,
      'transform-origin': 'center', // always rotate/scale from center, translate handles the focal panning
      'width': '100%',
      'height': '100%'
    };
  }
}
